/**
 * HypeFly · Aktionsseite-Content-Hook
 * -------------------------------------
 * Lädt `homepage_mode` (steuert, welche Seite auf `/` rendert) und
 * `aktion_page` (die editierbaren Texte der Aktions-Landing) aus dem
 * öffentlichen `/api/site-content?prefix=`-Endpoint.
 *
 * Robust gegen fehlende Backend-Docs: liefert immer sinnvolle
 * Defaults, damit `/aktion` auch vor dem ersten CMS-Save funktioniert.
 */
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

export type AktionFeature = {
    title: string;
    subtitle: string;
    /** lucide-react-Iconname (Kebab-Case, wird im Frontend gemappt). */
    icon: string;
};

export type AktionStep = {
    title: string;
    subtitle: string;
    icon: string;
};

export type AktionFaq = {
    q: string;
    a: string;
};

/** Typografie-Definition für ein Text-Element in der Special-Offer-Sektion. */
export type SpecialOfferTextSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
export type SpecialOfferFontFamily = "serif" | "sans" | "display" | "anton";
export type SpecialOfferFontWeight = "normal" | "bold" | "black";

export type SpecialOfferStyle = {
    size: SpecialOfferTextSize;
    color: string;
    font_family: SpecialOfferFontFamily;
    weight: SpecialOfferFontWeight;
    uppercase: boolean;
};

/**
 * Aktions-Angebot-Sektion: eigenständig, unabhängig vom Hero-Zähler und vom
 * Formular-CTA. Frei stylebar (Farbe, Schriftart, Größe, Fettung, Uppercase)
 * pro Text-Element. Optional ein/aus per Toggle.
 */
export type AktionSpecialOffer = {
    enabled: boolean;
    counter_enabled: boolean;
    counter_total: number;
    counter_taken: number;
    counter_label: string;
    headline: string;
    subline: string;
    cta_text: string;
    cta_target: "form" | "url";
    cta_url: string;
    headline_style: SpecialOfferStyle;
    subline_style: SpecialOfferStyle;
    cta_style: SpecialOfferStyle;
    background_color: string;
};

/**
 * Kontaktformular-Sektion (`/aktion` unten). Alle Texte + Feld-Labels +
 * Placeholders + Datenschutz-Text + Erfolgs-/Fehler-Meldungen sind
 * über den Admin editierbar. Typografie für Headline und Submit-Button
 * folgt dem `SpecialOfferStyle`-Muster.
 */
export type AktionContactForm = {
    headline: string;
    subline: string;
    name_label: string;
    name_placeholder: string;
    plz_label: string;
    plz_placeholder: string;
    phone_label: string;
    phone_placeholder: string;
    message_label: string;
    message_placeholder: string;
    privacy_text: string;        // enthält `{link}` als Platzhalter für den klickbaren Link
    privacy_link_text: string;
    privacy_link_url: string;
    submit_text: string;
    submit_target: "submit" | "url";
    submit_url?: string;
    success_message: string;
    error_message: string;
    headline_style: SpecialOfferStyle;
    submit_style: SpecialOfferStyle;
    background_color: string;
};

/** Ein einzelnes Medium in einer Vergleichs-Kachel. */
export type AktionComparisonItem = {
    media_type: "image" | "video" | "photo_360" | "video_360";
    /** Absolute oder relative URL (z.B. `/media/aktion/foo.jpg` oder `/api/media/{id}/file`). */
    src: string;
    /** Bei Medien aus der Mediathek: die Media-ID (nötig für 360°-Viewer-Tracking). */
    media_id?: string;
    /** Optionales Poster-Bild für Videos. */
    poster?: string;
};

/** Eine Vergleichs-Kachel: einzelnes Medium ODER Slideshow. */
export type AktionComparisonSlot = {
    slideshow_enabled: boolean;
    /** Sekunden zwischen Slide-Wechseln (1..30). */
    interval_seconds: number;
    items: AktionComparisonItem[];
};

/**
 * Gutschein-Popup / Promo-Modal auf `/aktion`.
 * ---------------------------------------------
 * Wird nach Zeit (`trigger_seconds`) oder nach Scroll-Prozent
 * (`trigger_scroll_percent`) angezeigt. Was zuerst greift, öffnet.
 * Werte auf 0 deaktivieren den jeweiligen Trigger.
 *
 * Dismissal via SessionStorage (`hf_gutschein_dismissed`) — nach
 * einmaligem Schließen kein erneutes Anzeigen bis Tab/Session-Reset.
 *
 * Countdown-Modi:
 *   • `"midnight"` → tickt zu heute 23:59:59 Ortszeit (rolling daily).
 *   • `"fixed"`    → tickt zu `countdown_target_iso` (Admin-Datum).
 *   • `"off"`      → kein Countdown, aber Popup bleibt sichtbar.
 */
export type GutscheinCountdownMode = "midnight" | "fixed" | "off";

export type GutscheinPopupConfig = {
    enabled: boolean;
    /** Sekunden auf der Seite bis das Popup öffnet. 0 = deaktiviert. */
    trigger_seconds: number;
    /** Scroll-Anteil (0..100) bis das Popup öffnet. 0 = deaktiviert. */
    trigger_scroll_percent: number;

    /** Header-Zeile klein oben, z.B. "HEUTE". */
    kicker: string;
    /** Haupt-Headline, z.B. "GROSSE GUTSCHEINAKTION". */
    headline: string;
    /** Rabatt-Prozent (Zahl, wird im Stern angezeigt), z.B. 80. */
    discount_percent: number;
    /** Text im Stern unter der Prozentzahl, z.B. "RABATT". */
    discount_label: string;
    /** Kurzer Body-Text unter dem Stern, z.B. "auf ALLE Leistungen". */
    body: string;
    /** Feindruck (Gültigkeit), z.B. "Rabatt nur heute buchbar • 4 Wochen einlösbar". */
    fine_print: string;

    /** Countdown-Modus. */
    countdown_mode: GutscheinCountdownMode;
    /** ISO-Zieldatum (nur für `countdown_mode === "fixed"`). */
    countdown_target_iso: string;
    /** Prefix vor der Countdown-Zahl, z.B. "noch". */
    countdown_prefix: string;
    /** Suffix hinter den Zahlen, z.B. "Std" bzw. "verbleibend". */
    countdown_suffix: string;

    /** Trust-Badges als Freitext-Liste (2 Boxen nebeneinander). */
    trust_badges: string[];

    /** CTA-Button-Text (bei aktivem Formular: Submit-Text). */
    cta_text: string;
    /** Reassurance-Zeile unter dem CTA-Button. */
    reassurance_line: string;

    /** "Später erinnern"-Text (schließt das Popup). */
    dismiss_text: string;
    /** Footer-Kleingedrucktes ganz unten. */
    footer_text: string;

    /* ── Mini-Formular im Popup ────────────────────────────
     * Wenn `form_enabled = true`, wird direkt im Modal ein
     * 3-Feld-Formular (Name/Telefon/PLZ + Datenschutz) gerendert,
     * das an `/api/contact` mit `source="gutschein"` sendet.
     * Wenn `form_enabled = false`, scrollt der CTA-Button zum
     * Haupt-Formular `#aktion-kontakt` und schließt das Popup. */
    form_enabled: boolean;
    form_headline: string;
    form_name_label: string;
    form_name_placeholder: string;
    form_phone_label: string;
    form_phone_placeholder: string;
    form_plz_label: string;
    form_plz_placeholder: string;
    /** Privacy-Text mit `{link}`-Platzhalter für den Datenschutz-Link. */
    form_privacy_text: string;
    form_privacy_link_text: string;
    form_privacy_link_url: string;
    /** Kampagnen-Tag (wird an das Backend als `campaign` gesendet, für Auswertung). */
    campaign_tag: string;

    /** Erfolgs-Meldung nach Formular-Submit. */
    form_success_headline: string;
    form_success_body: string;
};

export type AktionHeaderButtonConfig = {
    /** Text im kleinen Gold-Chip vor dem Button-Text. Default „AKTION". */
    chip_text: string;
    /** Haupttext des Buttons rechts vom Chip. Default „100 Häuser gesucht". */
    main_text: string;
    /** Icon-Toggle: wenn `true`, zeigt Flame-Icon im Chip. */
    show_icon: boolean;
};

export type HeroChoiceTilesConfig = {
    /** Master-Toggle: wenn `false`, werden die permanenten Auswahl-Kacheln
     *  neben dem „INFOS ERHALTEN"-Block ausgeblendet. Default: `true`. */
    enabled: boolean;
    /** Kachel 1 — Referenz-Weg (Gold). */
    card_referenz_title: string;
    card_referenz_subline: string;
    card_referenz_cta: string;
    /** Kachel 2 — Rabatt-Weg (Rot). */
    card_rabatt_title: string;
    card_rabatt_subline: string;
    card_rabatt_cta: string;
};

/**
 * Konfiguration einer einzelnen Video-Kachel im Hero (neben den
 * `HeroChoiceTiles`). Zwei Aspect-Ratios stehen bereit — im Preview
 * (autoplay muted loop) und im Fullscreen-Modal (mit Ton, kein Loop)
 * wird dieselbe URL abgespielt. `preferred_aspect` steuert, welche
 * URL gerendert wird — mit Fallback auf die jeweils andere.
 */
export type VideoTileConfig = {
    enabled: boolean;
    title: string;
    video_16x9_url: string;
    video_21x9_url: string;
    preferred_aspect: "16:9" | "21:9";
};

export type HeroVideoTilesConfig = {
    /** Master-Toggle für beide Video-Kacheln zusammen. */
    enabled: boolean;
    tile_1: VideoTileConfig;
    tile_2: VideoTileConfig;
};

export type VideoHeroConfig = {
    /** Master-Toggle. `false` = Video-Hero komplett unsichtbar, bestehendes
     *  Layout unverändert. */
    enabled: boolean;
    /** Cinema-Video 21:9 für Desktop (min-width ≥ 768 px). */
    video_desktop_url: string;
    /** Vertikales Video 9:16 für Mobile. Dient zusätzlich als Fallback,
     *  wenn `video_desktop_url` leer ist. */
    video_mobile_url: string;
    /** Optionales Poster-Bild — wird gezeigt, bis Video geladen ist. */
    poster_url: string;
    /** Video im Loop wiedergeben. */
    loop: boolean;
    /** „🔊 MIT TON ANSEHEN"-Button anzeigen. */
    show_sound_button: boolean;
    /** Beschriftung des Ton-Buttons (Zustand: stumm → ton an). */
    sound_button_text: string;
    /** Kleiner Indikator oben links, solange das Video stumm läuft. */
    muted_label: string;
};

export type WelcomeChoicePopupConfig = {
    /** Master-Toggle des Welcome-Choice-Popups. */
    enabled: boolean;
    /** Header-Zeile ganz oben (mit Emoji möglich). */
    headline: string;
    /** Untertitel-Zeile in Gold, kleiner Text darunter. */
    subline: string;
    /** Karte 1 (Referenz / 100 % KOSTENLOS): Titel, Body, CTA. */
    card_referenz_title: string;
    card_referenz_body: string;
    card_referenz_cta: string;
    /** Karte 2 (Rabatt / 80 %): Titel, Body, CTA. */
    card_rabatt_title: string;
    card_rabatt_body: string;
    card_rabatt_cta: string;
    /** Hilfe-Text unter den Karten. */
    helper_text: string;
    /** Sekundär-Link "Zuerst informieren". */
    dismiss_text: string;
    /** Delay bis das Popup erscheint (Millisekunden, nach Seiten-Load). */
    trigger_delay_ms: number;
    /** Sperr-Dauer nach Interaktion in Stunden (localStorage-basiert). */
    throttle_hours: number;
};

/**
 * Referenz-Popup (Dark-Modal, mode="referenz" von `GutscheinPopup`).
 * ---------------------------------------------------------------------
 * Eigenständige Konfiguration mit eigenem Layout (Gold-Zertifikats-
 * Siegel, ohne Countdown, ohne „€"-Wortlaut). Wird beim Klick auf die
 * Referenz-Karte im Welcome-Choice-Popup geöffnet. Sendet an
 * `/api/contact` mit `campaign="referenz-gutschein"` und
 * `discount_percent=100`.
 */
export type ReferenzPopupConfig = {
    /** Gold-Eyebrow ganz oben klein, z. B. „HEUTE". */
    kicker: string;
    /** Riesen-Titel (Anton), z. B. „GROSSE REFERENZ-AKTION". */
    headline: string;
    /** Text im Zertifikats-Siegel oben, z. B. „100 %". */
    seal_top: string;
    /** Text im Zertifikats-Siegel unten, z. B. „KOSTENLOS". */
    seal_bottom: string;
    /** Italic Serif-Zeile unter dem Siegel, z. B. „Ihr Haus als HypeFly-Referenz". */
    tagline: string;
    /** Trust-Chips (max. 4, 2 pro Zeile), z. B. „UNVERBINDLICH RESERVIEREN". */
    trust_badges: string[];
    /** Überschrift über dem Formular, z. B. „JETZT REFERENZPLATZ SICHERN". */
    form_headline: string;
    form_name_label: string;
    form_name_placeholder: string;
    form_phone_label: string;
    form_phone_placeholder: string;
    form_plz_label: string;
    form_plz_placeholder: string;
    /** Datenschutz-Text mit `{link}`-Platzhalter. */
    form_privacy_text: string;
    form_privacy_link_text: string;
    form_privacy_link_url: string;
    /** Gold-Submit-Button-Text, z. B. „REFERENZPLATZ UNVERBINDLICH SICHERN". */
    cta_text: string;
    /** Footer-Zeile ganz unten, z. B. „Sie sichern sich nur den Platz — kein Kauf, keine Verpflichtung." */
    footer_text: string;
    /** Erfolgs-Overlay Headline nach Submit. */
    form_success_headline: string;
    /** Erfolgs-Overlay Body-Text nach Submit. */
    form_success_body: string;
};

export type AktionPageConfig = {
    /**
     * Master-Switch für die komplette /aktion-Landing-Page.
     * WICHTIG: NICHT verwechseln mit `aktion.enabled` (steuert die Referenz-
     * Sektion + Banner auf der Startseite, editiert unter /admin/aktion).
     * Dieser Switch hier steuert die separate Route /aktion und den
     * Header-AKTION-CTA-Button. Editiert unter /admin/aktions-seite.
     * Default true (Backwards-Compat für Bestandsdaten ohne dieses Feld).
     */
    enabled: boolean;
    hero_headline: string;
    hero_brushstroke: string;
    hero_subline: string;
    seal_text: string;
    counter_total: number;
    counter_slots_taken: number;
    cta_primary_text: string;
    cta_final_text: string;
    features: AktionFeature[];
    warum_kostenlos: string;
    warum_kostenlos_title: string;
    demo_headline: string;
    demo_hint: string;
    demo_media_id: string;
    comparison_kicker: string;
    comparison_title: string;
    comparison_subline: string;
    comparison_vintage_label: string;
    comparison_modern_label: string;
    /** Legacy — wird nur noch für Migration gelesen. Bitte `comparison_left` nutzen. */
    comparison_vintage_image?: string;
    comparison_left: AktionComparisonSlot;
    comparison_right: AktionComparisonSlot;
    steps: AktionStep[];
    urgency_headline: string;
    /** Aktions-Angebot-Sektion (ersetzt die alte Urgency-Sektion im Render). */
    special_offer: AktionSpecialOffer;
    /** Editierbare Konfiguration für das Kontaktformular unten. */
    contact_form: AktionContactForm;
    /** Gutschein-/Promo-Popup (Modal auf `/aktion`). */
    gutschein_popup: GutscheinPopupConfig;
    /** Referenz-Popup (mode="referenz" von `GutscheinPopup`). Eigene Texte,
     *  eigenes Layout mit Gold-Zertifikats-Siegel, unabhängig vom
     *  Rabatt-Gutschein-Modus. */
    referenz_popup: ReferenzPopupConfig;
    /** Welcome-Choice-Popup — deaktiviert per Default, seit der
     *  Redesign-Runde ersetzt durch permanente Auswahl-Kacheln neben
     *  dem „INFOS ERHALTEN"-Block (s. `hero_choice_tiles`). Der Popup-
     *  Code + CMS-Editor bleiben erhalten, falls die Auto-Öffnung
     *  später wieder benötigt wird. */
    welcome_popup: WelcomeChoicePopupConfig;
    /** Permanente Auswahl-Kacheln neben dem Hero-CTA. */
    hero_choice_tiles: HeroChoiceTilesConfig;
    /** Zwei Video-Teaser-Kacheln neben den `hero_choice_tiles`. Klick öffnet
     *  ein Fullscreen-Modal mit Ton. Werden zusammen mit den Choice-Kacheln
     *  in einer 4er-Reihe (Desktop) bzw. gestacked (Mobile) gerendert. */
    hero_video_tiles: HeroVideoTilesConfig;
    /** Video-Hero ganz oben auf /aktion (über dem AUFGEPASST-Hero). Kann
     *  komplett deaktiviert werden, dann bleibt das ursprüngliche Layout
     *  unberührt. */
    video_hero: VideoHeroConfig;
    /** Beschriftung des AKTION-Buttons im globalen Header (Chip + Haupttext).
     *  Zeigt auf `/aktion`, sichtbar solange `aktion_page.enabled === true`. */
    header_button: AktionHeaderButtonConfig;
    faq_kicker: string;
    faq_title: string;
    faqs: AktionFaq[];
    updated_at?: string;
};

export type HomepageMode = "editorial" | "aktion";

const DEFAULT_CONFIG: AktionPageConfig = {
    enabled: true,
    hero_headline: "HAUSBESITZER",
    hero_brushstroke: "AUFGEPASST!",
    hero_subline:
        "Wir suchen 100 Häuser für unser neues Referenzprojekt — und Sie können dabei sein!",
    seal_text: "0€ FÜR AUSGEWÄHLTE HÄUSER · 100% KOSTENLOS",
    counter_total: 100,
    counter_slots_taken: 45,
    cta_primary_text: "INFOS ERHALTEN",
    cta_final_text: "JETZT KOSTENLOS SICHERN",
    features: [
        { title: "Komplette 360°-Aufnahmen", subtitle: "innen & außen", icon: "camera" },
        { title: "Interaktiver virtueller Rundgang", subtitle: "Ihr Haus – überall erlebbar", icon: "compass" },
        { title: "Professionelle Fotos & Videos", subtitle: "in höchster Qualität", icon: "film" },
        { title: "Ihr digitales Zuhause", subtitle: "für immer gespeichert", icon: "home" },
    ],
    warum_kostenlos_title: "WARUM KOSTENLOS?",
    warum_kostenlos:
        "Wir erstellen diese Aufnahmen für unsere HypeFly-Referenzserie und dürfen Ihr Projekt als Beispiel zeigen. Nichts weiter.",
    demo_headline: "So sieht Ihr Haus dann bei uns aus — schauen Sie selbst!",
    demo_hint: "",
    demo_media_id: "9a5407b6-7fe7-4c7a-a162-d4025d935987",
    comparison_kicker: "Damals ein Luxus. Heute für jeden.",
    comparison_title: "Von starren Luftbildern zu echten 360°-Erlebnissen.",
    comparison_subline:
        "Früher konnten sich nur wenige Familien ein Luftbild ihres Zuhauses leisten — als schwarz-weiß Fotografie im Goldrahmen an der Wand. Heute halten wir Ihr Haus in echter 360°-Rundumsicht fest, für jeden erschwinglich, dauerhaft für Sie und Ihre Familie.",
    comparison_vintage_label: "Damals — ein Luftbild als seltene Erinnerung",
    comparison_modern_label: "Heute — Ihr Zuhause modern dokumentiert",
    comparison_vintage_image: "/media/aktion/vintage-aerial-frame.jpg",
    comparison_left: {
        slideshow_enabled: true,
        interval_seconds: 3,
        items: [
            { media_type: "video", src: "/media/aktion/vintage-video-intro.mp4" },
            { media_type: "image", src: "/media/aktion/vintage-1930s.jpg" },
            { media_type: "image", src: "/media/aktion/vintage-1950s.jpg" },
            { media_type: "image", src: "/media/aktion/vintage-1970s.jpg" },
            { media_type: "image", src: "/media/aktion/vintage-1990s.jpg" },
            { media_type: "video", src: "/media/aktion/vintage-video-1.mp4" },
        ],
    },
    comparison_right: {
        slideshow_enabled: true,
        interval_seconds: 3,
        items: [
            {
                media_type: "video",
                src: "/media/aktion/heute-video-intro.mp4",
            },
            {
                media_type: "video",
                src: "/media/aktion/heute-video-2.mp4",
            },
            {
                media_type: "video",
                src: "/media/aktion/heute-video-main.mp4",
            },
        ],
    },
    steps: [
        { title: "Wir kommen zu Ihnen", subtitle: "& erstellen alles.", icon: "home" },
        { title: "Wir nehmen Ihr Haus in 360° auf", subtitle: "Innen & außen, professionell.", icon: "camera" },
        { title: "Wir erstellen Ihren virtuellen Rundgang", subtitle: "Perfekt aufbereitet.", icon: "cloud" },
        { title: "Sie erhalten Ihre 360°-Erlebniswelt", subtitle: "Für immer.", icon: "monitor" },
    ],
    urgency_headline: "Sichern Sie sich jetzt einen der letzten Referenz-Plätze.",
    special_offer: {
        enabled: true,
        counter_enabled: true,
        counter_total: 30,
        counter_taken: 14,
        counter_label: "noch frei",
        headline: "Sichern Sie sich jetzt einen der letzten Referenz-Plätze.",
        subline: "",
        cta_text: "JETZT KOSTENLOS SICHERN",
        cta_target: "form",
        cta_url: "",
        headline_style: { size: "2xl", color: "#0F1830", font_family: "sans", weight: "bold", uppercase: true },
        subline_style:  { size: "md",  color: "#0F1830", font_family: "sans", weight: "normal", uppercase: false },
        cta_style:      { size: "lg",  color: "#0F1830", font_family: "sans", weight: "black", uppercase: true },
        background_color: "#F6F1E9",
    },
    contact_form: {
        headline: "JETZT PLATZ SICHERN",
        subline: "Wir melden uns innerhalb 24 Stunden telefonisch bei Ihnen — unverbindlich und kostenlos.",
        name_label: "Name",
        name_placeholder: "",
        plz_label: "PLZ",
        plz_placeholder: "",
        phone_label: "Telefon",
        phone_placeholder: "",
        message_label: "Nachricht (optional)",
        message_placeholder: "Wann passt es Ihnen? Sonstige Anmerkungen?",
        privacy_text: "Ich habe die {link} gelesen und stimme der Verarbeitung meiner Daten zu.",
        privacy_link_text: "Datenschutzerklärung",
        privacy_link_url: "/datenschutz",
        submit_text: "Absenden",
        submit_target: "submit",
        submit_url: "",
        success_message: "Vielen Dank! Wir melden uns innerhalb 24 Stunden telefonisch bei Ihnen.",
        error_message: "Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.",
        headline_style: { size: "2xl", color: "#0F1830", font_family: "anton", weight: "black", uppercase: true },
        submit_style:   { size: "lg",  color: "#0F1830", font_family: "sans",  weight: "black", uppercase: true },
        background_color: "#FFFDF9",
    },
    gutschein_popup: {
        enabled: true,
        trigger_seconds: 10,
        trigger_scroll_percent: 30,
        kicker: "HEUTE",
        headline: "GROSSE GUTSCHEINAKTION",
        discount_percent: 80,
        discount_label: "RABATT",
        body: "auf ALLE Leistungen",
        fine_print: "Rabatt nur heute buchbar · 4 Wochen einlösbar",
        countdown_mode: "midnight",
        countdown_target_iso: "",
        countdown_prefix: "noch",
        countdown_suffix: "Std",
        trust_badges: [
            "UNVERBINDLICH RESERVIEREN",
            "KEINE BESTELLVERPFLICHTUNG",
        ],
        cta_text: "RABATT UNVERBINDLICH SICHERN",
        reassurance_line: "Sie sichern sich nur den Rabatt — kein Kauf, keine Verpflichtung.",
        dismiss_text: "Später erinnern",
        footer_text: "Keine Verpflichtung · Kostenlose Beratung",
        form_enabled: true,
        form_headline: "Jetzt Ihren Rabatt sichern",
        form_name_label: "Name",
        form_name_placeholder: "",
        form_phone_label: "Telefon",
        form_phone_placeholder: "",
        form_plz_label: "PLZ",
        form_plz_placeholder: "",
        form_privacy_text: "Ich habe die {link} gelesen und stimme der Verarbeitung meiner Daten zu.",
        form_privacy_link_text: "Datenschutzerklärung",
        form_privacy_link_url: "/datenschutz",
        campaign_tag: "gutschein_80",
        form_success_headline: "Reservierung eingegangen!",
        form_success_body: "Sie haben nun 4 Wochen Zeit, sich in Ruhe zu entscheiden. Wir rufen Sie unverbindlich zurück.",
    },
    welcome_popup: {
        enabled: false,
        headline: "🎁 WELCHEN HYPEFLY-VORTEIL MÖCHTEN SIE?",
        subline: "Nur für kurze Zeit zum Start von HypeFly.",
        card_referenz_title: "100 % KOSTENLOS",
        card_referenz_body: "Professionelle Innen- & Außenaufnahmen komplett kostenlos. Sie erlauben uns lediglich, ausgewählte Aufnahmen Ihres Projekts als HypeFly-Referenz zu zeigen.",
        card_referenz_cta: "REFERENZPLATZ SICHERN",
        card_rabatt_title: "80 % RABATT",
        card_rabatt_body: "Sie möchten nicht als Referenz erscheinen? Dann sichern Sie sich 80 % Rabatt auf Ihre HypeFly-Aufnahmen.",
        card_rabatt_cta: "80 % RABATT SICHERN",
        helper_text: "Kurz auswählen — weitere Informationen erhalten Sie direkt danach.",
        dismiss_text: "Zuerst informieren →",
        trigger_delay_ms: 500,
        throttle_hours: 24,
    },
    hero_choice_tiles: {
        enabled: true,
        card_referenz_title: "100 % KOSTENLOS",
        card_referenz_subline: "Als Referenzobjekt",
        card_referenz_cta: "UNVERBINDLICH SICHERN →",
        card_rabatt_title: "80 % NEUKUNDEN RABATT",
        card_rabatt_subline: "Ohne Referenz",
        card_rabatt_cta: "UNVERBINDLICH SICHERN →",
    },
    hero_video_tiles: {
        enabled: true,
        tile_1: {
            enabled: true,
            title: "HypeFly · Premium-Dokumentation",
            video_16x9_url: "",
            video_21x9_url:
                "https://customer-assets-v7afamib.emergentagent.net/job_hypefly-demo/artifacts/77utg1mn_a98491b4a2bc4fa9b068cd5514da98bc_hypefly_gold2_21x9.mp4",
            preferred_aspect: "21:9",
        },
        tile_2: {
            enabled: true,
            title: "Für Generationen bewahrt",
            video_16x9_url:
                "https://customer-assets-v7afamib.emergentagent.net/job_hypefly-demo/artifacts/i0ur2093_66ca57c3b9e44ca19c4354d0a1a5eba5_hypefly_generationen_final-German.mp4",
            video_21x9_url: "",
            preferred_aspect: "16:9",
        },
    },
    video_hero: {
        enabled: true,
        video_desktop_url:
            "https://customer-assets-jai6qajn.emergentagent.net/wingman/eac399ce-3e91-4bf7-8503-8e953433b21a/attachments/d0efd1c34ec5472d8d7f8457c635863e_hypefly_final_21x9.mp4",
        video_mobile_url:
            "https://customer-assets-jai6qajn.emergentagent.net/wingman/eac399ce-3e91-4bf7-8503-8e953433b21a/attachments/790317c14aae49f787f8cad9b650e6ae_hypefly_final_v7.mp4",
        poster_url: "",
        loop: true,
        show_sound_button: true,
        sound_button_text: "🔊 MIT TON ANSEHEN",
        muted_label: "STUMM · Klick für Ton",
    },
    referenz_popup: {
        kicker: "HEUTE",
        headline: "GROSSE REFERENZ-AKTION",
        seal_top: "100 %",
        seal_bottom: "KOSTENLOS",
        tagline: "Ihr Haus als HypeFly-Referenz",
        trust_badges: [
            "UNVERBINDLICH RESERVIEREN",
            "KEINE KOSTEN FÜR SIE",
        ],
        form_headline: "JETZT REFERENZPLATZ SICHERN",
        form_name_label: "Name",
        form_name_placeholder: "",
        form_phone_label: "Telefon",
        form_phone_placeholder: "",
        form_plz_label: "PLZ",
        form_plz_placeholder: "",
        form_privacy_text: "Ich habe die {link} gelesen und stimme der Verarbeitung meiner Daten zu.",
        form_privacy_link_text: "Datenschutzerklärung",
        form_privacy_link_url: "/datenschutz",
        cta_text: "REFERENZPLATZ UNVERBINDLICH SICHERN",
        footer_text: "Sie sichern sich nur den Platz — kein Kauf, keine Verpflichtung.",
        form_success_headline: "Referenzplatz reserviert!",
        form_success_body: "Wir rufen Sie innerhalb von 24 Stunden unverbindlich zurück, um Ihren Aufnahme-Termin zu besprechen.",
    },
    header_button: {
        chip_text: "Aktion",
        main_text: "100 Häuser gesucht",
        show_icon: true,
    },
    faq_kicker: "Häufige Fragen",
    faq_title: "Kein Haken. Keine Verpflichtung. Nur klare Antworten.",
    faqs: [
        {
            q: "Ist das wirklich komplett kostenlos?",
            a: "Ja, für ausgewählte Referenz-Häuser zu 100%. Keine versteckten Kosten, keine Nachberechnung. Das ist Teil unserer Marketing-Investition.",
        },
        {
            q: "Wo ist der Haken?",
            a: "Kein Haken. Wir dürfen Ihre Aufnahmen als Referenz-Beispiel für neue Kunden zeigen — mehr wollen wir nicht. Ihre Adresse und persönlichen Daten bleiben natürlich privat.",
        },
        {
            q: "Was passiert mit den fertigen Aufnahmen?",
            a: "Sie erhalten von uns ein Speichermedium mit allen Dateien — Fotos, Videos und 360°-Aufnahmen. Zusätzlich bekommen Sie einen privaten Online-Link, mit dem Sie das Haus jederzeit besuchen und mit Ihrer Familie teilen können. Die Dateien gehören dauerhaft Ihnen.",
        },
        {
            q: "Wie lange dauert die Aufnahme vor Ort?",
            a: "Das wird individuell mit Ihnen abgesprochen — je nach Größe des Hauses und Ihren Wünschen.",
        },
        {
            q: "Ist mein Haus dann öffentlich im Internet sichtbar?",
            a: "Nein. Nur wenn Sie ausdrücklich zustimmen, zeigen wir anonymisierte Ausschnitte in unserem Referenz-Portfolio. Ihre Aufnahmen sind grundsätzlich passwortgeschützt.",
        },
        {
            q: "Bis wann ist die Aktion gültig?",
            a: "Solange freie Plätze verfügbar sind (aktueller Stand siehe Zähler oben). Sobald 100 Häuser dokumentiert sind, endet die Aktion.",
        },
        {
            q: "Wer steckt hinter HypeFly?",
            a: "HypeFly ist eine Marke der BDV GmbH aus Düsseldorf. Persönliche Betreuung, deutsche Datenschutz-Standards, langjährige Erfahrung.",
        },
        {
            q: "Was passiert nach meiner Anfrage?",
            a: "Wir melden uns innerhalb von 24 Stunden telefonisch, klären kurz Ihre Wünsche und schlagen einen Aufnahme-Termin vor. Alles unverbindlich.",
        },
    ],
};

async function fetchSiteContent(key: string): Promise<Record<string, unknown> | null> {
    try {
        // Public endpoint liefert `{items: {key: value}}` (Objekt, nicht Array).
        const res = await fetch(`${BACKEND_URL}/api/site-content?prefix=${encodeURIComponent(key)}`, {
            credentials: "omit",
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { items?: Record<string, unknown> };
        const items = data.items || {};
        const v = items[key];
        return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
    } catch {
        return null;
    }
}

export function useHomepageMode(): { mode: HomepageMode; loaded: boolean } {
    const [mode, setMode] = useState<HomepageMode>("editorial");
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const v = await fetchSiteContent("homepage_mode");
            if (cancelled) return;
            const m = v && typeof v.mode === "string" ? v.mode : "editorial";
            setMode(m === "aktion" ? "aktion" : "editorial");
            setLoaded(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    return { mode, loaded };
}

export function useAktionsSeite(): AktionPageConfig {
    const [cfg, setCfg] = useState<AktionPageConfig>(DEFAULT_CONFIG);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const v = await fetchSiteContent("aktion_page");
            if (cancelled || !v) return;
            setCfg((prev) => migrateComparison({ ...prev, ...(v as Partial<AktionPageConfig>) }));
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    return cfg;
}

/**
 * Migriert Legacy-Configs (mit `comparison_vintage_image` und `demo_media_id`,
 * aber ohne `comparison_left`/`comparison_right`) in die neue Slot-Struktur.
 * Wenn die neuen Felder bereits sinnvoll gefüllt sind, wird unverändert
 * zurückgegeben.
 */
function migrateComparison(cfg: AktionPageConfig): AktionPageConfig {
    const hasLeft = cfg.comparison_left && Array.isArray(cfg.comparison_left.items) && cfg.comparison_left.items.length > 0;
    const hasRight = cfg.comparison_right && Array.isArray(cfg.comparison_right.items) && cfg.comparison_right.items.length > 0;
    // Special-Offer-Migration: wenn Feld fehlt → Defaults, wenn möglich mit
    // Übernahme der alten urgency_headline.
    const so = cfg.special_offer as AktionSpecialOffer | undefined;
    const migratedSo: AktionSpecialOffer = so && typeof so === "object"
        ? { ...DEFAULT_CONFIG.special_offer, ...so,
            headline_style: { ...DEFAULT_CONFIG.special_offer.headline_style, ...(so.headline_style || {}) },
            subline_style:  { ...DEFAULT_CONFIG.special_offer.subline_style,  ...(so.subline_style  || {}) },
            cta_style:      { ...DEFAULT_CONFIG.special_offer.cta_style,      ...(so.cta_style      || {}) },
        }
        : {
            ...DEFAULT_CONFIG.special_offer,
            headline: cfg.urgency_headline || DEFAULT_CONFIG.special_offer.headline,
            cta_text: cfg.cta_final_text   || DEFAULT_CONFIG.special_offer.cta_text,
            counter_total: cfg.counter_total || DEFAULT_CONFIG.special_offer.counter_total,
            counter_taken: cfg.counter_slots_taken || DEFAULT_CONFIG.special_offer.counter_taken,
        };
    // Contact-Form-Migration: wenn Feld fehlt → Defaults, wenn möglich mit
    // Übernahme der alten cta_final_text.
    const cf = cfg.contact_form as AktionContactForm | undefined;
    // Defensiver Alt-Wert-Fix: bestehende Live-DB-Docs haben
    // `submit_text = "JETZT KOSTENLOS SICHERN →"` (oder Varianten davon).
    // Wenn wir diesen Alt-Wert erkennen, überschreiben wir ihn mit dem
    // neuen Default „Absenden" — sonst bleibt der CMS-Wert erhalten
    // (spätere Anpassungen im Admin haben Vorrang).
    const LEGACY_SUBMIT_TEXTS = [
        "JETZT KOSTENLOS SICHERN →",
        "JETZT KOSTENLOS SICHERN",
        "Jetzt kostenlos sichern →",
        "Jetzt kostenlos sichern",
    ];
    const migratedCf: AktionContactForm = cf && typeof cf === "object"
        ? { ...DEFAULT_CONFIG.contact_form, ...cf,
            headline_style: { ...DEFAULT_CONFIG.contact_form.headline_style, ...(cf.headline_style || {}) },
            submit_style:   { ...DEFAULT_CONFIG.contact_form.submit_style,   ...(cf.submit_style   || {}) },
            submit_text: LEGACY_SUBMIT_TEXTS.includes((cf.submit_text || "").trim())
                ? DEFAULT_CONFIG.contact_form.submit_text
                : (cf.submit_text || DEFAULT_CONFIG.contact_form.submit_text),
        }
        : {
            ...DEFAULT_CONFIG.contact_form,
            // Bei ganz alten Configs (ohne `contact_form`-Sub-Objekt) wurde
            // früher `cta_final_text` als Fallback übernommen. Auch hier
            // Legacy-Wert filtern.
            submit_text: cfg.cta_final_text && !LEGACY_SUBMIT_TEXTS.includes(cfg.cta_final_text.trim())
                ? cfg.cta_final_text
                : DEFAULT_CONFIG.contact_form.submit_text,
        };
    // Gutschein-Popup-Migration: wenn Feld fehlt (Legacy-Config vor Popup-
    // Rollout) → Defaults übernehmen. Vorhandene Felder werden gemerged,
    // damit neue Sub-Keys (z.B. neue Trust-Badges) rückwirkend erscheinen.
    const gp = (cfg as { gutschein_popup?: GutscheinPopupConfig }).gutschein_popup;
    const migratedGp: GutscheinPopupConfig = gp && typeof gp === "object"
        ? {
            ...DEFAULT_CONFIG.gutschein_popup,
            ...gp,
            trust_badges: Array.isArray(gp.trust_badges) && gp.trust_badges.length > 0
                ? gp.trust_badges
                : DEFAULT_CONFIG.gutschein_popup.trust_badges,
        }
        : { ...DEFAULT_CONFIG.gutschein_popup };
    // Welcome-Choice-Popup-Migration: analog Gutschein-Popup.
    const wp = (cfg as { welcome_popup?: WelcomeChoicePopupConfig }).welcome_popup;
    const migratedWp: WelcomeChoicePopupConfig = wp && typeof wp === "object"
        ? { ...DEFAULT_CONFIG.welcome_popup, ...wp }
        : { ...DEFAULT_CONFIG.welcome_popup };
    // Referenz-Popup-Migration: analog Gutschein-Popup. Backwards-Compat für
    // Bestandsdaten ohne dieses Feld → volle Defaults.
    const rp = (cfg as { referenz_popup?: ReferenzPopupConfig }).referenz_popup;
    const migratedRp: ReferenzPopupConfig = rp && typeof rp === "object"
        ? {
            ...DEFAULT_CONFIG.referenz_popup,
            ...rp,
            trust_badges: Array.isArray(rp.trust_badges) && rp.trust_badges.length > 0
                ? rp.trust_badges
                : DEFAULT_CONFIG.referenz_popup.trust_badges,
        }
        : { ...DEFAULT_CONFIG.referenz_popup };
    // Header-Button-Migration (fehlender Key = Defaults).
    const hb = (cfg as { header_button?: AktionHeaderButtonConfig }).header_button;
    const migratedHb: AktionHeaderButtonConfig = hb && typeof hb === "object"
        ? { ...DEFAULT_CONFIG.header_button, ...hb }
        : { ...DEFAULT_CONFIG.header_button };
    // Video-Hero-Migration (fehlender Key = Defaults inkl. der beiden URLs).
    const vh = (cfg as { video_hero?: VideoHeroConfig }).video_hero;
    const migratedVh: VideoHeroConfig = vh && typeof vh === "object"
        ? { ...DEFAULT_CONFIG.video_hero, ...vh }
        : { ...DEFAULT_CONFIG.video_hero };
    // Hero-Choice-Tiles-Migration.
    const ht = (cfg as { hero_choice_tiles?: HeroChoiceTilesConfig }).hero_choice_tiles;
    const migratedHt: HeroChoiceTilesConfig = ht && typeof ht === "object"
        ? { ...DEFAULT_CONFIG.hero_choice_tiles, ...ht }
        : { ...DEFAULT_CONFIG.hero_choice_tiles };
    // Hero-Video-Tiles-Migration (fehlender Key = Defaults inkl. beider
    // Test-Video-URLs). Vorhandene Kacheln werden pro Slot gemerged, damit
    // Bestandsdaten (z. B. Titel-Änderungen) erhalten bleiben, aber neue
    // Sub-Keys automatisch mit Defaults gefüllt werden.
    const hvt = (cfg as { hero_video_tiles?: HeroVideoTilesConfig }).hero_video_tiles;
    const migratedHvt: HeroVideoTilesConfig = hvt && typeof hvt === "object"
        ? {
            ...DEFAULT_CONFIG.hero_video_tiles,
            ...hvt,
            tile_1: { ...DEFAULT_CONFIG.hero_video_tiles.tile_1, ...(hvt.tile_1 || {}) },
            tile_2: { ...DEFAULT_CONFIG.hero_video_tiles.tile_2, ...(hvt.tile_2 || {}) },
        }
        : { ...DEFAULT_CONFIG.hero_video_tiles };
    const base: AktionPageConfig = {
        ...cfg,
        special_offer: migratedSo,
        contact_form: migratedCf,
        gutschein_popup: migratedGp,
        welcome_popup: migratedWp,
        referenz_popup: migratedRp,
        header_button: migratedHb,
        video_hero: migratedVh,
        hero_choice_tiles: migratedHt,
        hero_video_tiles: migratedHvt,
        // Master-Switch: Backwards-Compat — fehlender Key = an.
        enabled: (cfg as { enabled?: boolean }).enabled !== false,
    };
    if (hasLeft && hasRight) return base;
    return {
        ...base,
        comparison_left: hasLeft ? base.comparison_left : {
            slideshow_enabled: false,
            interval_seconds: 3,
            items: [{
                media_type: "image",
                src: base.comparison_vintage_image || DEFAULT_CONFIG.comparison_left.items[0].src,
            }],
        },
        comparison_right: hasRight ? base.comparison_right : {
            slideshow_enabled: false,
            interval_seconds: 3,
            items: [{
                media_type: "video_360",
                media_id: base.demo_media_id,
                src: "",
            }],
        },
    };
}

export const DEFAULT_AKTION_PAGE_CONFIG = DEFAULT_CONFIG;
